import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  ChevronLeft,
  MapPin,
  Users,
  Award,
  History,
  XCircle,
  Clock,
  DollarSign,
  ArrowUpDown,
  Ship,
  Plane,
  Truck,
  Train,
  Play,
  Pause,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { MetricCard, PageHeader, KPIStrip } from '@/components/common';
import {
  rfqApi,
  FreightRFQ as FreightRFQType,
  FreightRFQLine,
  FreightRFQInvitation,
  FreightRFQResponse,
  FreightRFQAward,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { formatDate } from '@/lib/utils';

const statusColors: Record<string, string> = {
  Draft: 'bg-muted text-muted-foreground border-border',
  Open: 'bg-primary-50 text-primary-500 border-primary-200',
  Closed: 'bg-warning-50 text-warning-600 border-warning-200',
  Awarded: 'bg-success-50 text-success-600 border-success-200',
  Cancelled: 'bg-danger-50 text-danger-600 border-danger-200',
};

const shipmentTypeIcons: Record<string, React.ReactNode> = {
  OCEAN_FCL: <Ship className="w-4 h-4" />,
  OCEAN_LCL: <Ship className="w-4 h-4" />,
  AIR: <Plane className="w-4 h-4" />,
  FTL: <Truck className="w-4 h-4" />,
  LTL: <Truck className="w-4 h-4" />,
  RAIL: <Train className="w-4 h-4" />,
  BREAK_BULK: <Ship className="w-4 h-4" />,
  INTERMODAL: <Truck className="w-4 h-4" />,
};

export default function FreightRFQDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rfq, setRfq] = useState<FreightRFQType | null>(null);
  const [lines, setLines] = useState<FreightRFQLine[]>([]);
  const [invitations, setInvitations] = useState<FreightRFQInvitation[]>([]);
  const [responses, setResponses] = useState<FreightRFQResponse[]>([]);
  const [awards, setAwards] = useState<FreightRFQAward[]>([]);
  const loadRFQ = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await rfqApi.getById(id);
      setRfq(data);
      setLines(data.lines || []);
      setInvitations(data.invitations || []);
    } catch (error) {
      console.error('Failed to load RFQ:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadRFQ();
    }
  }, [id, loadRFQ]);

  const loadResponses = async () => {
    if (!id) return;
    try {
      const data = await rfqApi.getResponses(id);
      setResponses(data);
    } catch (error) {
      console.error('Failed to load responses:', error);
    }
  };

  const loadAwards = async () => {
    if (!id) return;
    try {
      const data = await rfqApi.getAwards(id);
      setAwards(data);
    } catch (error) {
      console.error('Failed to load awards:', error);
    }
  };

  const handleStatusAction = async (action: 'send' | 'close' | 'cancel') => {
    if (!id) return;
    try {
      switch (action) {
        case 'send':
          await rfqApi.send(id);
          toast.success('RFQ opened and sent to carriers');
          break;
        case 'close':
          await rfqApi.close(id);
          toast.success('RFQ closed');
          break;
        case 'cancel':
          await rfqApi.cancel(id);
          toast.success('RFQ cancelled');
          break;
      }
      loadRFQ();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(getErrorMessage(error));
    }
  };

  if (loading || !rfq) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/tms/rfq')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const daysToDeadline = Math.max(
    0,
    Math.ceil((new Date(rfq.responseDeadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
  );

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp} className="mb-6">
        {/* Header */}
        <PageHeader
          backLink={{ label: 'Back to Freight RFQ', href: '/tms/rfq' }}
          title={rfq.title}
          description={rfq.description || `RFQ ${rfq.rfqNumber}`}
          actions={
            <div className="flex items-center gap-3">
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[rfq.status]}`}
              >
                {rfq.status}
              </span>
              {rfq.shipmentType && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-muted rounded text-xs">
                  {shipmentTypeIcons[rfq.shipmentType]}
                  {rfq.shipmentType.replace('_', ' ')}
                </span>
              )}
              {rfq.status === 'Draft' && (
                <>
                  {/* An "Edit" button used to sit here, pointing at
                      /tms/rfq/:id/edit — a route that has never existed, so it
                      answered 404 every time. rfqApi.update() is implemented,
                      but no edit form was ever built and FreightRFQCreate does
                      not accept an id. Removed rather than left broken; the
                      button can come back with the form behind it. */}
                  <Button onClick={() => handleStatusAction('send')}>
                    <Play className="w-4 h-4 mr-2" />
                    Open RFQ
                  </Button>
                </>
              )}
              {rfq.status === 'Open' && (
                <>
                  <Button variant="outline" onClick={() => handleStatusAction('close')}>
                    <Pause className="w-4 h-4 mr-2" />
                    Close RFQ
                  </Button>
                  <Button variant="destructive" onClick={() => handleStatusAction('cancel')}>
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </>
              )}
              {(rfq.status === 'Open' || rfq.status === 'Closed') && (
                <Button variant="outline" onClick={() => navigate(`/tms/rfq/${id}/compare`)}>
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  Compare Quotes
                </Button>
              )}
              {rfq.status === 'Closed' && (
                <Button onClick={() => navigate(`/tms/rfq/${id}/compare`)}>
                  <Award className="w-4 h-4 mr-2" />
                  Manage Awards
                </Button>
              )}
            </div>
          }
        />
      </motion.div>

      {/* KPIs */}
      <div>
        <KPIStrip columns={4}>
          <MetricCard label="Lanes" value={lines.length} icon={<MapPin className="w-5 h-5" />} />
          <MetricCard
            label="Quotes Received"
            value={rfq.totalQuotesReceived || 0}
            icon={<DollarSign className="w-5 h-5" />}
            trend={{ direction: 'neutral', value: `${invitations.length} carriers invited` }}
          />
          <MetricCard
            label="Lowest Bid"
            value={rfq.lowestBidAmount ? `$${rfq.lowestBidAmount.toLocaleString()}` : '-'}
            icon={<ArrowUpDown className="w-5 h-5" />}
          />
          <MetricCard
            label="Days to Deadline"
            value={daysToDeadline}
            icon={<Clock className="w-5 h-5" />}
          />
        </KPIStrip>
      </div>

      {/* Tabs */}
      <motion.div variants={fadeUp}>
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="lanes">Lanes</TabsTrigger>
            <TabsTrigger value="carriers">Carriers</TabsTrigger>
            <TabsTrigger value="quotes" onClick={loadResponses}>
              Quotes
            </TabsTrigger>
            <TabsTrigger value="awards" onClick={loadAwards}>
              Awards
            </TabsTrigger>
            <TabsTrigger value="audit">Audit</TabsTrigger>
          </TabsList>

          <div className={cn(PANEL, 'mt-6 p-6')}>
            <TabsContent value="overview">
              <OverviewTab rfq={rfq} lines={lines} invitations={invitations} />
            </TabsContent>
            <TabsContent value="lanes">
              <LanesTab lines={lines} />
            </TabsContent>
            <TabsContent value="carriers">
              <CarriersTab invitations={invitations} />
            </TabsContent>
            <TabsContent value="quotes">
              <QuotesTab responses={responses} lines={lines} />
            </TabsContent>
            <TabsContent value="awards">
              <AwardsTab awards={awards} lines={lines} />
            </TabsContent>
            <TabsContent value="audit">
              <AuditTab rfqId={id!} />
            </TabsContent>
          </div>
        </Tabs>
      </motion.div>
    </motion.main>
  );
}

// Tab Components
function OverviewTab({
  rfq,
  lines,
  invitations,
}: {
  rfq: FreightRFQType;
  lines: FreightRFQLine[];
  invitations: FreightRFQInvitation[];
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        {/* RFQ Details */}
        <div className="space-y-4">
          <h3 className="text-base font-semibold">RFQ Details</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Type</span>
              <p className="font-medium">{rfq.type}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Shipment Type</span>
              <p className="font-medium">{rfq.shipmentType?.replace('_', ' ') || '-'}</p>
            </div>
            {rfq.incoterm && (
              <div>
                <span className="text-muted-foreground">Incoterm</span>
                <p className="font-medium">{rfq.incoterm}</p>
              </div>
            )}
          </div>
        </div>

        {/* Dates */}
        <div className="space-y-4">
          <h3 className="text-base font-semibold">Timeline</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Response Deadline</span>
              <p className="font-medium">{formatDate(rfq.responseDeadline)}</p>
            </div>
            {rfq.awardTargetDate && (
              <div>
                <span className="text-muted-foreground">Award Target</span>
                <p className="font-medium">{formatDate(rfq.awardTargetDate)}</p>
              </div>
            )}
            {rfq.rateEffectiveDate && (
              <div>
                <span className="text-muted-foreground">Rate Effective</span>
                <p className="font-medium">{formatDate(rfq.rateEffectiveDate)}</p>
              </div>
            )}
            {rfq.rateExpirationDate && (
              <div>
                <span className="text-muted-foreground">Rate Expiration</span>
                <p className="font-medium">{formatDate(rfq.rateExpirationDate)}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cargo Info */}
      {rfq.cargoDescription && (
        <div className="border-t pt-6">
          <h3 className="text-base font-semibold mb-3">Cargo Information</h3>
          <p className="text-muted-foreground">{rfq.cargoDescription}</p>
          <div className="flex gap-4 mt-3">
            {rfq.hazmatRequired && (
              <span className="px-2 py-1 bg-status-warning-subtle text-status-warning-foreground rounded text-sm">
                Hazmat Required
              </span>
            )}
            {rfq.temperatureControlled && (
              <span className="px-2 py-1 bg-status-info-subtle text-status-info-foreground rounded text-sm">
                Temperature Controlled
              </span>
            )}
          </div>
        </div>
      )}

      {/* Quick Summary */}
      <div className="border-t pt-6">
        <h3 className="text-base font-semibold mb-3">Summary</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-muted rounded-lg p-4">
            <div className="text-2xl font-bold">{lines.length}</div>
            <div className="text-sm text-muted-foreground">Lanes</div>
          </div>
          <div className="bg-muted rounded-lg p-4">
            <div className="text-2xl font-bold">{invitations.length}</div>
            <div className="text-sm text-muted-foreground">Carriers Invited</div>
          </div>
          <div className="bg-muted rounded-lg p-4">
            <div className="text-2xl font-bold">
              {invitations.filter((i) => i.status === 'Responded').length}
            </div>
            <div className="text-sm text-muted-foreground">Responses</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LanesTab({ lines }: { lines: FreightRFQLine[] }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Lanes ({lines.length})</h3>
      </div>
      <div className="space-y-3">
        {lines.map((line) => (
          <div key={line.id} className="border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-muted-foreground">
                  #{line.lineNumber}
                </span>
                <div>
                  <div className="font-medium">
                    {line.originCity}, {line.originState}
                    <span className="mx-2 text-muted-foreground">→</span>
                    {line.destCity}, {line.destState}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {line.mode} • {line.equipmentType || 'N/A'}
                  </div>
                </div>
              </div>
              <div className="text-right">
                {line.targetRate && (
                  <div className="text-sm">Target: ${line.targetRate.toLocaleString()}</div>
                )}
                {line.award && (
                  <span className="text-xs text-status-success bg-status-success-subtle px-2 py-0.5 rounded">
                    Awarded
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
        {lines.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed rounded-xl">
            <MapPin className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No lanes added</p>
          </div>
        )}
      </div>
    </div>
  );
}

function CarriersTab({ invitations }: { invitations: FreightRFQInvitation[] }) {
  const statusBadges: Record<string, string> = {
    Pending: 'bg-muted text-status-neutral border-border',
    Sent: 'bg-status-info-subtle text-status-info border-status-info-border',
    Viewed: 'bg-status-warning-subtle text-status-warning border-status-warning-border',
    Responded: 'bg-status-success-subtle text-status-success border-status-success-border',
    Declined:
      'bg-status-destructive-subtle text-status-destructive border-status-destructive-border',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Invited Carriers ({invitations.length})</h3>
        <Button variant="outline">
          <Users className="w-4 h-4 mr-2" />
          Invite More
        </Button>
      </div>
      <div className="space-y-2">
        {invitations.map((inv) => (
          <div key={inv.id} className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-4">
              <div>
                <div className="font-medium">{inv.carrier.name}</div>
                <div className="text-xs leading-4 text-muted-foreground">{inv.carrier.code}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadges[inv.status]}`}
              >
                {inv.status}
              </span>
              {inv.viewedAt && (
                <span className="text-xs text-muted-foreground">
                  Viewed {formatDate(inv.viewedAt)}
                </span>
              )}
            </div>
          </div>
        ))}
        {invitations.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed rounded-xl">
            <Users className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No carriers invited</p>
          </div>
        )}
      </div>
    </div>
  );
}

function QuotesTab({
  responses,
  lines,
}: {
  responses: FreightRFQResponse[];
  lines: FreightRFQLine[];
}) {
  const groupedByLine = lines.map((line) => ({
    line,
    quotes: responses.filter((r) => r.rfqLineId === line.id),
  }));

  return (
    <div className="space-y-6">
      <h3 className="text-base font-semibold">Quotes by Lane</h3>
      {groupedByLine.map(({ line, quotes }) => (
        <div key={line.id} className="border rounded-lg overflow-hidden">
          <div className="bg-muted px-4 py-3 border-b">
            <div className="font-medium">
              Lane #{line.lineNumber}: {line.originCity} → {line.destCity}
            </div>
            <div className="text-sm text-muted-foreground">{quotes.length} quote(s) received</div>
          </div>
          {quotes.length > 0 ? (
            <div className="divide-y">
              {quotes.map((quote) => (
                <div key={quote.id} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium">
                      {quote.invitation?.carrier.name || 'Unknown Carrier'}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Base: ${quote.baseRate.toLocaleString()} • Total: $
                      {quote.totalRate?.toLocaleString() || quote.baseRate.toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                        quote.status === 'Submitted'
                          ? 'bg-status-info-subtle text-status-info border-status-info-border'
                          : quote.status === 'Accepted'
                            ? 'bg-status-success-subtle text-status-success border-status-success-border'
                            : 'bg-muted text-status-neutral border-border'
                      }`}
                    >
                      {quote.status}
                    </span>
                    <Button variant="outline" size="sm">
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No quotes received for this lane
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AwardsTab({ awards, lines }: { awards: FreightRFQAward[]; lines: FreightRFQLine[] }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Awards ({awards.length})</h3>
      </div>
      {awards.length > 0 ? (
        <div className="space-y-3">
          {awards.map((award) => {
            const line = lines.find((l) => l.id === award.rfqLineId);
            return (
              <div
                key={award.id}
                className="border rounded-lg p-4 flex items-center justify-between"
              >
                <div>
                  <div className="font-medium">
                    Lane #{line?.lineNumber}: {line?.originCity} → {line?.destCity}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Awarded to: {award.carrier?.name} • Rate: $
                    {award.awardedBaseRate.toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                      award.status === 'Published'
                        ? 'bg-status-success-subtle text-status-success border-status-success-border'
                        : award.status === 'Reviewed'
                          ? 'bg-status-info-subtle text-status-info border-status-info-border'
                          : 'bg-status-warning-subtle text-status-warning border-status-warning-border'
                    }`}
                  >
                    {award.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 border-2 border-dashed rounded-xl">
          <Award className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
          <p className="text-muted-foreground">No awards yet</p>
        </div>
      )}
    </div>
  );
}

function AuditTab({ rfqId: _rfqId }: { rfqId: string }) {
  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold">Audit Trail</h3>
      <div className="text-center py-12 border-2 border-dashed rounded-xl">
        <History className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
        <p className="text-muted-foreground">Audit log loading...</p>
      </div>
    </div>
  );
}
